import type { AiToolCallView } from './types'

type ConfirmationToolCall = Pick<
  AiToolCallView,
  'resultJson' | 'resultViewJson' | 'status' | 'errorMessage'
>

function confirmationEnvelope(call: ConfirmationToolCall): Record<string, unknown> | null {
  for (const raw of [call.resultJson, call.resultViewJson]) {
    if (!raw) continue
    try {
      const parsed = JSON.parse(raw) as unknown
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      // An invalid renderer projection is ordinary tool data; try the other representation.
    }
  }
  return null
}

/** True while a write call is represented by a separate V2 confirmation card. */
function isAwaitingToolConfirmation(call: ConfirmationToolCall): boolean {
  if (call.status === 'NEEDS_CONFIRMATION') return true
  const envelope = confirmationEnvelope(call)
  if (!envelope) return false
  const gateway = envelope.gateway
  const status = envelope.confirmationStatus
  return gateway === 'NEEDS_CONFIRMATION'
    && (status == null || status === 'PENDING')
}

/** A declined or expired confirmation did not execute a business operation. */
function isNonExecutedConfirmationOutcome(call: ConfirmationToolCall): boolean {
  const envelope = confirmationEnvelope(call)
  if (!envelope) return false
  const status = typeof envelope.confirmationStatus === 'string'
    ? envelope.confirmationStatus.toUpperCase()
    : ''
  if (['CANCELLED', 'CANCELED', 'REJECTED', 'DECLINED', 'EXPIRED'].includes(status)) return true
  return envelope.routeType === 'USER_REFUSED'
}

/** Only executed operations belong in the ordinary operation timeline. */
function isTimelineVisibleToolCall(call: ConfirmationToolCall): boolean {
  return !isAwaitingToolConfirmation(call) && !isNonExecutedConfirmationOutcome(call)
}

/**
 * 工具调用传输成功但业务结果为失败的显式标记检测。
 *
 * 与后端 ToolResultDataStatusDetector.looksLikeError 对齐（只取无歧义标记）：
 * 顶层或 data 层出现 success===false / error 非空字符串 / status==="error"。
 * execute_code 批量写回执（第三步退役 2026-09-23）的终态视图不再携带 success，
 * 改由三态声明 outcome 表达：partial / failed 均为业务失败（completed 不是）。
 * 旧数据（无 outcome 的历史归一化视图）仍由 success 判定，两种形态并存兼容。
 * 未识别内容保守按非失败处理（不改变既有展示）。
 */
function hasExplicitBusinessFailure(resultJson: string | null | undefined): boolean {
  if (!resultJson) return false
  let parsed: unknown
  try {
    parsed = JSON.parse(resultJson)
  } catch {
    return false
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false
  const sources: Array<Record<string, unknown>> = [parsed as Record<string, unknown>]
  const inner = (parsed as Record<string, unknown>).data
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    sources.push(inner as Record<string, unknown>)
  }
  for (const source of sources) {
    if (source.outcome === 'partial' || source.outcome === 'failed') return true
    if (source.success === false) return true
    if (typeof source.error === 'string' && source.error.trim()) return true
    if (typeof source.status === 'string' && source.status.toLowerCase() === 'error') return true
  }
  return false
}

export {
  isAwaitingToolConfirmation,
  isNonExecutedConfirmationOutcome,
  isTimelineVisibleToolCall,
  hasExplicitBusinessFailure
}
